import { useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useRealtimeSelfHeal } from '@/hooks/useRealtimeSelfHeal';

export interface BrokerOffer {
  id: string;
  load_id: string;
  company_id: string;
  rate_amount: number;
  status: string;
  expires_at: string | null;
  created_at: string;
  created_by: string;
  notes: string | null;
  carrier_profile?: {
    first_name: string | null;
    last_name: string | null;
    email: string;
    phone: string | null;
  };
  load?: {
    load_number: string;
    origin_city: string;
    origin_state: string;
    destination_city: string;
    destination_state: string;
    rate_amount: number | null;
  };
}

const fetchBrokerOffers = async (userId: string): Promise<BrokerOffer[]> => {
  const { data, error } = await supabase
    .from('offers')
    .select(`
      id, load_id, company_id, rate_amount, status, expires_at, created_at, created_by, notes,
      loads!inner (load_number, origin_city, origin_state, destination_city, destination_state, rate_amount, created_by)
    `)
    .in('status', ['pending', 'accepted_by_carrier', 'countered', 'accepted', 'rejected'])
    .order('created_at', { ascending: false })
    .limit(100);

  if (error) throw error;

  const userOffers = (data || []).filter((offer: any) => offer.loads?.created_by === userId);

  const carrierIds = [...new Set(userOffers.map((o: any) => o.created_by))];
  let profilesMap: Record<string, any> = {};

  if (carrierIds.length > 0) {
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, first_name, last_name, email, phone')
      .in('id', carrierIds);

    if (profiles) {
      profilesMap = profiles.reduce((acc, p) => { acc[p.id] = p; return acc; }, {} as Record<string, any>);
    }
  }

  return userOffers.map((offer: any) => ({
    id: offer.id,
    load_id: offer.load_id,
    company_id: offer.company_id,
    rate_amount: offer.rate_amount,
    status: offer.status,
    expires_at: offer.expires_at,
    created_at: offer.created_at,
    created_by: offer.created_by,
    notes: offer.notes ?? null,
    carrier_profile: profilesMap[offer.created_by] ? {
      first_name: profilesMap[offer.created_by].first_name,
      last_name: profilesMap[offer.created_by].last_name,
      email: profilesMap[offer.created_by].email,
      phone: profilesMap[offer.created_by].phone,
    } : undefined,
    load: offer.loads ? {
      load_number: offer.loads.load_number,
      origin_city: offer.loads.origin_city,
      origin_state: offer.loads.origin_state,
      destination_city: offer.loads.destination_city,
      destination_state: offer.loads.destination_state,
      rate_amount: offer.loads.rate_amount,
    } : undefined,
  }));
};

export function useBrokerOffers() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: offers = [], isLoading: loading } = useQuery({
    queryKey: ['broker-offers', user?.id],
    queryFn: () => fetchBrokerOffers(user!.id),
    enabled: !!user,
    staleTime: 30_000,
  });

  // Realtime: any UPDATE on the offers table — invalidate the broker's offers
  // query so the page reflects carrier counter / accept / reject / re-counter
  // without waiting on a refresh. The fetcher already scopes to this broker's
  // own loads, so an unscoped channel is safe and cheap.
  const userId = user?.id;
  const invalidateOffers = useCallback(() => {
    if (!userId) return;
    queryClient.invalidateQueries({ queryKey: ['broker-offers', userId] });
  }, [userId, queryClient]);
  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel(`broker-offers-updates-${userId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'offers' },
        () => { invalidateOffers(); }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') invalidateOffers();
      });
    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, invalidateOffers]);
  useRealtimeSelfHeal(invalidateOffers, !!userId);


  const respondToOffer = async (offerId: string, action: 'accept' | 'decline' | 'counter', counterAmount?: number, notes?: string) => {
    try {
      const { data, error } = await supabase.rpc('broker_respond_to_offer', {
        p_offer_id: offerId,
        p_action: action,
        p_counter_amount: counterAmount || null,
        p_notes: notes?.trim() || null,
      });

      if (error) throw error;

      const result = data as { success: boolean; error?: string };

      if (!result.success) {
        const msg = result.error || 'An error occurred';
        // Race-condition: the offer was already resolved by another path.
        // Treat as a soft refresh, not a red error.
        if (msg.toLowerCase().includes('no longer active') || msg.toLowerCase().includes('not found')) {
          toast({ title: 'Already resolved', description: 'This offer was just updated. Refreshing…' });
          await queryClient.invalidateQueries({ queryKey: ['broker-offers', user?.id] });
          return false;
        }
        toast({ variant: 'destructive', title: 'Error', description: msg });
        return false;
      }

      const actionMessages = {
        accept: 'Load awarded to carrier! A notification has been sent.',
        decline: 'Offer declined. The carrier has been notified.',
        counter: 'Counter offer sent to carrier.',
      };

      toast({ title: 'Success', description: actionMessages[action] });
      await queryClient.invalidateQueries({ queryKey: ['broker-offers', user?.id] });
      return true;
    } catch (error: any) {
      console.error('Error responding to offer:', error);
      toast({ variant: 'destructive', title: 'Error', description: error.message || 'Failed to respond to offer' });
      return false;
    }
  };

  return {
    offers,
    loading,
    respondToOffer,
    refetch: () => queryClient.invalidateQueries({ queryKey: ['broker-offers', user?.id] }),
  };
}
